import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { LoadingOverlay } from '@/components/feedback';
import { useAppDispatch } from '@/app/hooks';
import { ROUTES } from '@/app/routes';
import { AuthLayout } from '../components/AuthLayout';
import { LoginForm } from '../components/LoginForm';
import { OtpForm } from '../components/OtpForm';
import {
  useLoginMutation,
  useGetOauthRedirectUrlMutation,
  useOauthUserLoginMutation,
} from '../api/authApi';
import { sessionEstablished } from '../constants/sessionSlice';
import { useTranslations } from '@/hooks';

const OAUTH_STATE_STORAGE_KEY = 'oauth_state';

// The whole auth flow lives in one page/route with a local step state.
export const LoginPage = () => {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleForgotPassword = useCallback(() => {
    navigate(ROUTES.forgotPassword);
  }, [navigate]);
  const handleDismissResendNotice = useCallback(() => {
    setResendNotice(false);
  }, []);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp'
  const [credentials, setCredentials] = useState(null);
  const [resendNotice, setResendNotice] = useState(false);
  const [isOauthLoading, setIsOauthLoading] = useState(false);
  const [oauthError, setOauthError] = useState(null);

  const [login, { isLoading: isSubmitting, error: submitError }] = useLoginMutation();
  const [resendLogin, { isLoading: isResending, error: resendError }] = useLoginMutation();
  const [getOauthRedirectUrl] = useGetOauthRedirectUrlMutation();
  const [oauthUserLogin] = useOauthUserLoginMutation();

  const handleCredentialsSubmit = async (values) => {
    const result = await login(values);
    if (result.error) return;
    setCredentials(values);
    setStep('otp');
  };

  const handleOtpSubmit = async (otp) => {
    const result = await login({ ...credentials, otp });
    if (result.error || !result.data?.authenticated) return;
    dispatch(sessionEstablished(result.data.user));
    navigate(location.state?.from?.pathname ?? ROUTES.home, { replace: true });
  };

  const handleResend = async () => {
    const result = await resendLogin(credentials);
    if (!result.error) setResendNotice(true);
  };

  const handleSocialLogin = async (provider) => {
    setOauthError(null);
    setIsOauthLoading(true);
    const result = await getOauthRedirectUrl(provider);
    if (result.error) {
      setIsOauthLoading(false);
      setOauthError(result.error);
      return;
    }
    localStorage.setItem(OAUTH_STATE_STORAGE_KEY, result.data.state);
    window.location.href = result.data.redirectUrl;
  };

  const oauthHandledRef = useRef(false);
  useEffect(() => {
    if (oauthHandledRef.current) return;
    const code = searchParams.get('code');
    if (!code) return;
    oauthHandledRef.current = true;

    const state = searchParams.get('state');
    const storedState = localStorage.getItem(OAUTH_STATE_STORAGE_KEY);
    localStorage.removeItem(OAUTH_STATE_STORAGE_KEY);
    setSearchParams({}, { replace: true }); // strip code/state either way — it's single-use

    if (!state || state !== storedState) return;
    const provider = state.includes('google')
      ? 'google'
      : state.includes('microsoft')
        ? 'microsoft'
        : null;
    if (!provider) return;

    setIsOauthLoading(true);
    oauthUserLogin({ provider, code }).then((result) => {
      setIsOauthLoading(false);
      if (result.error || !result.data?.authenticated) {
        setOauthError(result.error ?? { data: { message: 'Sign-in failed. Please try again.' } });
        return;
      }
      dispatch(sessionEstablished(result.data.user));
      navigate(location.state?.from?.pathname ?? ROUTES.home, { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isOauthLoading) {
    return (
      <AuthLayout>
        <LoadingOverlay label={t('AUTH_SIGNING_IN')} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {step === 'credentials' ? (
        <LoginForm
          onSubmit={handleCredentialsSubmit}
          isLoading={isSubmitting}
          error={submitError ?? oauthError}
          onSocialLogin={handleSocialLogin}
          onForgotPassword={handleForgotPassword}
        />
      ) : (
        <OtpForm
          email={credentials.email}
          onSubmit={handleOtpSubmit}
          onResend={handleResend}
          isLoading={isSubmitting}
          isResending={isResending}
          error={submitError ?? resendError}
          resendNotice={resendNotice}
          onDismissResendNotice={handleDismissResendNotice}
        />
      )}
    </AuthLayout>
  );
};
